import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { SimWorld } from '../../simulation/types/SimWorld';
import { isGroupModeEnabled } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';
import type { GroupId, GroupConfig } from '../../simulation/types/Group';

interface NPCListPanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

type SortType = 'birthDay' | 'generation' | 'rating' | 'winRate' | 'name';
type FilterType = 'all' | 'alive' | 'dead' | 'immigrant' | 'native';

// 仮想スクロール設定
const ROW_HEIGHT = 42; // 各行の高さ（px）
const ROW_HEIGHT_MOBILE = 52; // モバイル用（px）
const BUFFER_COUNT = 10; // 上下に余分に描画する行数

export const NPCListPanel: React.FC<NPCListPanelProps> = ({ world, onNPCClick, isMobile = false }) => {
  const [sortBy, setSortBy] = useState<SortType>('birthDay');
  const [sortAsc, setSortAsc] = useState(true);
  const [filterBy, setFilterBy] = useState<FilterType>('all');
  const [selectedGeneration, setSelectedGeneration] = useState<number | 'all'>('all');
  const [selectedGroup, setSelectedGroup] = useState<GroupId | 'all'>('all');
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(300);
  const containerRef = useRef<HTMLDivElement>(null);

  // グループモードの状態
  const groupModeActive = isGroupModeEnabled(world);
  const groups: GroupConfig[] = world.groupModeConfig?.groups || [];

  // グループIDから設定を取得するヘルパー
  const getGroupById = useCallback((groupId: GroupId | undefined): GroupConfig | undefined => {
    if (!groupId) return undefined;
    return groups.find(g => g.id === groupId);
  }, [groups]);

  // 全世代を取得
  const generations = useMemo(() => {
    const gens = new Set<number>();
    world.npcs.forEach((npc) => gens.add(npc.generation || 1));
    return Array.from(gens).sort((a, b) => a - b);
  }, [world.npcs]);

  // フィルタリング & ソート
  const filteredNPCs = useMemo(() => {
    let npcs = [...world.npcs];

    // フィルタ適用
    switch (filterBy) {
      case 'alive':
        npcs = npcs.filter((n) => n.isAlive);
        break;
      case 'dead':
        npcs = npcs.filter((n) => !n.isAlive);
        break;
      case 'immigrant':
        npcs = npcs.filter((n) => n.birthDay > 0 && n.family.filter((f) => f.relationType === 'parent').length === 0);
        break;
      case 'native':
        npcs = npcs.filter((n) => n.birthDay === 0 || n.family.filter((f) => f.relationType === 'parent').length > 0);
        break;
    }

    // 世代フィルタ
    if (selectedGeneration !== 'all') {
      npcs = npcs.filter((n) => (n.generation || 1) === selectedGeneration);
    }

    // グループフィルタ
    if (groupModeActive && selectedGroup !== 'all') {
      npcs = npcs.filter((n) => n.group === selectedGroup);
    }

    // ソート
    npcs.sort((a, b) => {
      let result = 0;
      switch (sortBy) {
        case 'birthDay':
          result = (a.birthDay || 0) - (b.birthDay || 0);
          break;
        case 'generation':
          result = (a.generation || 1) - (b.generation || 1);
          break;
        case 'rating':
          result = a.rating - b.rating;
          break;
        case 'winRate':
          const aRate = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
          const bRate = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
          result = aRate - bRate;
          break;
        case 'name':
          result = a.name.localeCompare(b.name, 'ja');
          break;
      }
      return sortAsc ? result : -result;
    });

    return npcs;
  }, [world.npcs, filterBy, selectedGeneration, selectedGroup, groupModeActive, sortBy, sortAsc]);

  // 統計
  const stats = useMemo(() => {
    const immigrants = world.npcs.filter(
      (n) => n.birthDay > 0 && n.family.filter((f) => f.relationType === 'parent').length === 0
    );
    return {
      total: world.npcs.length,
      alive: world.npcs.filter((n) => n.isAlive).length,
      dead: world.npcs.filter((n) => !n.isAlive).length,
      immigrants: immigrants.length,
    };
  }, [world.npcs]);

  // コンテナサイズの監視
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeight = () => {
      setContainerHeight(container.clientHeight);
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // スクロールハンドラ
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setScrollTop(target.scrollTop);
  }, []);

  const handleSort = (type: SortType) => {
    if (sortBy === type) {
      setSortAsc(!sortAsc);
    } else {
      setSortBy(type);
      setSortAsc(true);
    }
  };

  // 仮想スクロール: 表示範囲の計算
  const effectiveRowHeight = isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT;
  const totalHeight = filteredNPCs.length * effectiveRowHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / effectiveRowHeight) - BUFFER_COUNT);
  const visibleCount = Math.ceil(containerHeight / effectiveRowHeight) + BUFFER_COUNT * 2;
  const endIndex = Math.min(filteredNPCs.length, startIndex + visibleCount);
  const visibleNPCs = filteredNPCs.slice(startIndex, endIndex);
  const offsetY = startIndex * effectiveRowHeight;

  // モバイル用スタイル
  const getSummaryStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.summary,
        flexWrap: 'wrap',
        gap: '8px',
        padding: '8px',
        fontSize: '12px',
      };
    }
    return styles.summary;
  };

  const getFilterBarStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.filterBar,
        flexDirection: 'column',
        gap: '8px',
      };
    }
    return styles.filterBar;
  };

  const getSortBarStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.sortBar,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        flexWrap: 'nowrap',
      };
    }
    return styles.sortBar;
  };

  const sortOptions = isMobile
    ? [
        { key: 'rating', label: 'Rt' },
        { key: 'winRate', label: '勝率' },
        { key: 'name', label: '名前' },
      ]
    : [
        { key: 'birthDay', label: '生まれた日' },
        { key: 'generation', label: '世代' },
        { key: 'rating', label: 'Rating' },
        { key: 'winRate', label: '勝率' },
        { key: 'name', label: '名前' },
      ];

  return (
    <div style={styles.container}>
      {/* 統計サマリー */}
      <div style={getSummaryStyle()}>
        <span>総数: {stats.total}</span>
        <span>生存: {stats.alive}</span>
        <span>死亡: {stats.dead}</span>
        <span>移住者: {stats.immigrants}</span>
      </div>

      {/* フィルタ */}
      <div style={getFilterBarStyle()}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>状態:</label>
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value as FilterType)}
            style={isMobile ? { ...styles.select, flex: 1 } : styles.select}
          >
            <option value="all">すべて</option>
            <option value="alive">生存のみ</option>
            <option value="dead">死亡のみ</option>
            <option value="immigrant">移住者のみ</option>
            <option value="native">土着のみ</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>世代:</label>
          <select
            value={selectedGeneration}
            onChange={(e) =>
              setSelectedGeneration(e.target.value === 'all' ? 'all' : parseInt(e.target.value))
            }
            style={isMobile ? { ...styles.select, flex: 1 } : styles.select}
          >
            <option value="all">すべて</option>
            {generations.map((gen) => (
              <option key={gen} value={gen}>
                第{gen}世代
              </option>
            ))}
          </select>
        </div>

        {/* グループフィルター（グループモード時のみ） */}
        {groupModeActive && (
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>グループ:</label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value as GroupId | 'all')}
              style={isMobile ? { ...styles.select, flex: 1 } : styles.select}
            >
              <option value="all">すべて</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id} style={{ color: group.color }}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ソートボタン */}
      <div style={getSortBarStyle()}>
        <span style={styles.sortLabel}>ソート:</span>
        {sortOptions.map(({ key, label }) => (
          <button
            key={key}
            style={{
              ...styles.sortButton,
              ...(sortBy === key ? styles.sortButtonActive : {}),
              ...(isMobile ? { padding: '4px 8px', fontSize: '11px', flexShrink: 0 } : {}),
            }}
            onClick={() => handleSort(key as SortType)}
          >
            {label} {sortBy === key && (sortAsc ? '▲' : '▼')}
          </button>
        ))}
      </div>

      {/* NPCリスト */}
      <div style={styles.listWrapper}>
        {!isMobile && (
          <div style={styles.listHeader}>
            <span style={styles.colStatus}>状態</span>
            <span style={styles.colName}>名前</span>
            {groupModeActive && <span style={styles.colGroup}>グループ</span>}
            <span style={styles.colGen}>世代</span>
            <span style={styles.colBirth}>誕生日</span>
            <span style={styles.colRecord}>戦績</span>
            <span style={styles.colFame}>Rating</span>
            <span style={styles.colWinRate}>勝率</span>
          </div>
        )}
        <div
          ref={containerRef}
          style={styles.listContainer}
          onScroll={handleScroll}
        >
          {filteredNPCs.length === 0 ? (
            <div style={styles.emptyMessage}>条件に一致するNPCがありません</div>
          ) : (
            <div style={{ height: totalHeight, position: 'relative' }}>
              <div style={{ transform: `translateY(${offsetY}px)` }}>
                {visibleNPCs.map((npc) => {
                  const totalBattles = npc.wins + npc.losses;
                  const winRate = totalBattles > 0 ? ((npc.wins / totalBattles) * 100).toFixed(1) : '-';
                  const isImmigrant =
                    npc.birthDay > 0 && npc.family.filter((f) => f.relationType === 'parent').length === 0;
                  const npcGroup = getGroupById(npc.group);

                  if (isMobile) {
                    // モバイル: カード形式
                    return (
                      <div
                        key={npc.id}
                        style={{
                          ...styles.mobileListItem,
                          height: effectiveRowHeight,
                          opacity: npc.isAlive ? 1 : 0.5,
                          cursor: onNPCClick ? 'pointer' : 'default',
                          borderLeft: npcGroup ? `3px solid ${npcGroup.color}` : undefined,
                        }}
                        onClick={() => onNPCClick?.(npc)}
                      >
                        <span style={styles.mobileStatus}>{npc.isAlive ? '🟢' : '💀'}</span>
                        <div style={styles.mobileInfo}>
                          <div style={styles.mobileName}>
                            {npc.name}
                            {npcGroup && (
                              <span style={{ ...styles.groupBadge, backgroundColor: npcGroup.color }}>
                                {npcGroup.id}
                              </span>
                            )}
                            {isImmigrant && <span style={styles.immigrantBadge}>移住</span>}
                          </div>
                          <div style={styles.mobileSubInfo}>
                            第{npc.generation || 1}世代 | {totalBattles}戦{npc.wins}勝
                          </div>
                        </div>
                        <div style={styles.mobileStats}>
                          <div style={styles.mobileRating}>{npc.rating}</div>
                          <div
                            style={{
                              ...styles.mobileWinRate,
                              color:
                                winRate === '-'
                                  ? '#666'
                                  : parseFloat(winRate) >= 50
                                  ? '#4ade80'
                                  : '#ef4444',
                            }}
                          >
                            {winRate === '-' ? '-' : `${winRate}%`}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // デスクトップ: テーブル形式
                  return (
                    <div
                      key={npc.id}
                      style={{
                        ...styles.listItem,
                        height: effectiveRowHeight,
                        opacity: npc.isAlive ? 1 : 0.5,
                        cursor: onNPCClick ? 'pointer' : 'default',
                      }}
                      onClick={() => onNPCClick?.(npc)}
                    >
                      <span style={styles.colStatus}>{npc.isAlive ? '🟢' : '💀'}</span>
                      <span style={styles.colName}>
                        {npc.name}
                        {isImmigrant && <span style={styles.immigrantBadge}>移住</span>}
                      </span>
                      {groupModeActive && (
                        <span style={styles.colGroup}>
                          {npcGroup ? (
                            <span style={{ ...styles.groupBadge, backgroundColor: npcGroup.color }}>
                              {npcGroup.id}
                            </span>
                          ) : (
                            '-'
                          )}
                        </span>
                      )}
                      <span style={styles.colGen}>第{npc.generation || 1}世代</span>
                      <span style={styles.colBirth}>
                        {npc.birthDay === 0 ? '初期' : `Day ${npc.birthDay}`}
                      </span>
                      <span style={styles.colRecord}>{totalBattles}戦{npc.wins}勝</span>
                      <span style={styles.colFame}>{npc.rating}</span>
                      <span
                        style={{
                          ...styles.colWinRate,
                          color:
                            winRate === '-'
                              ? '#666'
                              : parseFloat(winRate) >= 50
                              ? '#4ade80'
                              : '#ef4444',
                        }}
                      >
                        {winRate === '-' ? '-' : `${winRate}%`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={styles.footer}>
        表示: {filteredNPCs.length} / {world.npcs.length}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  summary: {
    display: 'flex',
    gap: '16px',
    padding: '12px',
    background: '#16162a',
    borderRadius: '6px',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#aaa',
  },
  filterBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '13px',
    color: '#888',
  },
  select: {
    padding: '6px 10px',
    fontSize: '13px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#ddd',
    cursor: 'pointer',
  },
  sortBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  sortLabel: {
    fontSize: '13px',
    color: '#888',
  },
  sortButton: {
    padding: '4px 10px',
    fontSize: '12px',
    background: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#aaa',
    cursor: 'pointer',
  },
  sortButtonActive: {
    background: '#3a3a5e',
    color: '#fff',
    borderColor: '#667eea',
  },
  listWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#16162a',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  listContainer: {
    flex: 1,
    overflow: 'auto',
  },
  listHeader: {
    display: 'flex',
    padding: '10px 12px',
    borderBottom: '1px solid #444',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#888',
    background: '#16162a',
    flexShrink: 0,
  },
  listItem: {
    display: 'flex',
    padding: '10px 12px',
    borderBottom: '1px solid #2a2a3e',
    fontSize: '13px',
    color: '#ddd',
    transition: 'background 0.2s',
    boxSizing: 'border-box',
    alignItems: 'center',
  },
  emptyMessage: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px 20px',
  },
  colStatus: {
    width: '40px',
    flexShrink: 0,
  },
  colName: {
    flex: 1,
    minWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  colGroup: {
    width: '70px',
    flexShrink: 0,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  colGen: {
    width: '70px',
    flexShrink: 0,
    textAlign: 'center',
  },
  colBirth: {
    width: '80px',
    flexShrink: 0,
    textAlign: 'center',
  },
  colRecord: {
    width: '80px',
    flexShrink: 0,
    textAlign: 'center',
    fontSize: '12px',
  },
  colFame: {
    width: '50px',
    flexShrink: 0,
    textAlign: 'right',
  },
  colWinRate: {
    width: '60px',
    flexShrink: 0,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  immigrantBadge: {
    fontSize: '10px',
    padding: '2px 6px',
    background: '#764ba2',
    borderRadius: '10px',
    color: '#fff',
  },
  groupBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '10px',
    color: '#fff',
    fontWeight: 'bold',
    textShadow: '0 0 2px rgba(0,0,0,0.5)',
  },
  footer: {
    padding: '8px 12px',
    fontSize: '12px',
    color: '#666',
    textAlign: 'right',
  },
  // モバイル用スタイル
  mobileListItem: {
    display: 'flex',
    padding: '8px 12px',
    borderBottom: '1px solid #2a2a3e',
    fontSize: '13px',
    color: '#ddd',
    transition: 'background 0.2s',
    boxSizing: 'border-box',
    alignItems: 'center',
    gap: '10px',
  },
  mobileStatus: {
    fontSize: '16px',
    flexShrink: 0,
  },
  mobileInfo: {
    flex: 1,
    minWidth: 0,
  },
  mobileName: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '2px',
  },
  mobileSubInfo: {
    fontSize: '11px',
    color: '#888',
  },
  mobileStats: {
    textAlign: 'right',
    flexShrink: 0,
  },
  mobileRating: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
  },
  mobileWinRate: {
    fontSize: '11px',
    fontWeight: 'bold',
  },
};
